const PRODUCTION_CMS_API_URL = 'https://manage.jriit.ac.tz/api/public'

/**
 * Prefer explicit env, otherwise infer a sensible local dev default.
 *
 * When REACT_APP_USE_PROXY=true, use relative /api/public so the CRA dev server
 * proxies requests to the remote CMS. This avoids CORS and can improve reliability
 * when the direct connection to the remote server is unstable.
 */
export function getCmsApiUrl() {
  // Proxy mode: use relative URL so dev server forwards to proxy target
  if (process.env.REACT_APP_USE_PROXY === 'true') return '/api/public'

  if (process.env.REACT_APP_CMS_API_URL) return process.env.REACT_APP_CMS_API_URL

  // If we don't have a browser (tests/build), use production default.
  if (typeof window === 'undefined') return PRODUCTION_CMS_API_URL

  const host = window.location.hostname
  const protocol = window.location.protocol

  // Known production hosts (frontend-only; API at manage.jriit.ac.tz)
  if (host === 'jriit.beetletz.online' || host === 'new.jriit.ac.tz' || host === 'jriit.ac.tz') {
    return process.env.REACT_APP_CMS_API_URL || 'https://manage.jriit.ac.tz/api/public'
  }

  // Local/staging over http: assume CMS is on same host port 3000.
  if (protocol === 'http:') return `http://${host}:3000/api/public`

  // Otherwise, fall back to production unless explicitly configured.
  return PRODUCTION_CMS_API_URL
}

/**
 * Origin for CMS static files (/uploads/...). Must match setupProxy target when REACT_APP_USE_PROXY=true
 * (set REACT_APP_CMS_BASE_URL to your CMS, e.g. http://localhost:3000 for local CMS).
 */
export function getCmsOriginForUploads() {
  const b = (process.env.REACT_APP_CMS_BASE_URL || '').replace(/\/$/, '')
  return b || 'https://manage.jriit.ac.tz'
}

const CMS_API_URL = getCmsApiUrl()

class CMSApiService {
  // Generic fetch method with error handling
  async fetchFromCMS(endpoint) {
    try {
      const response = await fetch(`${CMS_API_URL}${endpoint}`)
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch from CMS')
      }
      
      return data
    } catch (error) {
      console.error(`Error fetching from CMS ${endpoint}:`, error)
      return { success: false, error: error.message }
    }
  }

  // Active banner announcements for homepage ticker
  async getAnnouncements() {
    return await this.fetchFromCMS('/announcements?type=banner')
  }

  // Active popup ads for homepage modal
  async getPopupAds() {
    return await this.fetchFromCMS('/announcements?type=popup')
  }

  // Departments (courses)
  async getDepartments() {
    return await this.fetchFromCMS('/departments')
  }

  // Homepage components
  async getHomepageComponents() {
    return await this.fetchFromCMS('/homepage-components')
  }

  async getHomepageComponentByType(type) {
    return await this.fetchFromCMS(`/homepage-components?type=${type}`)
  }

  // Hero media (image/video support)
  async getHeroMedia() {
    return await this.fetchFromCMS('/homepage/hero')
  }

  // Hero slides
  async getHeroSlides() {
    return await this.fetchFromCMS('/hero-slides')
  }

  // Page components
  async getPageComponents(pageName, includeInactive = false) {
    const inactive = includeInactive ? '&includeInactive=true' : ''
    const url = `/page-components?pageName=${encodeURIComponent(pageName)}${inactive}`
    return await this.fetchFromCMS(url)
  }

  async getPageComponent(pageName, componentName) {
    return await this.fetchFromCMS(`/page-components?pageName=${pageName}&componentName=${componentName}`)
  }

  // Specific page content methods
  async getAboutPageContent() {
    return await this.getPageComponents('about')
  }

  async getProgramsPageContent() {
    return await this.getPageComponents('programs')
  }

  async getContactPageContent() {
    return await this.getPageComponents('contact')
  }

  async getAdmissionPageContent() {
    return await this.getPageComponents('admission')
  }

  // Component-specific methods
  async getHeroContent(pageName = 'homepage') {
    const result = await this.getPageComponent(pageName, 'hero')
    return result.success ? result.components[0] : null
  }

  async getAboutSectionContent() {
    const result = await this.getPageComponent('about', 'about-section')
    return result.success ? result.components[0] : null
  }

  async getMissionVisionContent() {
    const result = await this.getPageComponent('about', 'mission-vision')
    return result.success ? result.components[0] : null
  }

  async getContactFormContent() {
    const result = await this.getPageComponent('contact', 'contact-form')
    return result.success ? result.components[0] : null
  }

  async getContactInfoContent() {
    const result = await this.getPageComponent('contact', 'contact-info')
    return result.success ? result.components[0] : null
  }

  async getProgramsListContent() {
    const result = await this.getPageComponent('programs', 'programs-list')
    return result.success ? result.components[0] : null
  }

  async getAdmissionRequirementsContent() {
    const result = await this.getPageComponent('admission', 'requirements')
    return result.success ? result.components[0] : null
  }

  // Programs
  async getPrograms(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.department) queryParams.append('department', params.department)
    if (params.departmentSlug) queryParams.append('departmentSlug', params.departmentSlug)
    if (params.search) queryParams.append('search', params.search)
    if (params.featured) queryParams.append('featured', params.featured)
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    
    const queryString = queryParams.toString()
    return await this.fetchFromCMS(`/programs${queryString ? `?${queryString}` : ''}`)
  }

  async getProgramsByDepartment(departmentSlug) {
    return await this.getPrograms({ departmentSlug })
  }

  async getProgramBySlug(slug) {
    // Use the public API endpoint
    return await this.fetchFromCMS(`/programs/${slug}`)
  }

  // Information Technology page specific
  async getInformationTechnologyPageContent() {
    return await this.getPageComponents('information-technology')
  }

  // News methods
  async getNews(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.search) queryParams.append('search', params.search)
    if (params.category) queryParams.append('category', params.category)
    if (params.featured) queryParams.append('featured', params.featured)
    if (params.breaking) queryParams.append('breaking', params.breaking)
    
    const queryString = queryParams.toString()
    return await this.fetchFromCMS(`/news${queryString ? `?${queryString}` : ''}`)
  }

  // Events methods
  async getEvents(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.search) queryParams.append('search', params.search)
    if (params.category) queryParams.append('category', params.category)
    if (params.upcoming) queryParams.append('upcoming', params.upcoming)
    if (params.featured) queryParams.append('featured', params.featured)
    
    const queryString = queryParams.toString()
    return await this.fetchFromCMS(`/events${queryString ? `?${queryString}` : ''}`)
  }

  // Gallery methods
  async getGalleryPhotos(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    
    const queryString = queryParams.toString()
    return await this.fetchFromCMS(`/gallery/photos${queryString ? `?${queryString}` : ''}`)
  }

  async getGalleryVideos(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    
    const queryString = queryParams.toString()
    return await this.fetchFromCMS(`/gallery/videos${queryString ? `?${queryString}` : ''}`)
  }

  // Global settings
  async getGlobalSettings() {
    return await this.fetchFromCMS('/settings')
  }

  // Utility methods
  async isCMSAvailable() {
    try {
      const response = await fetch(`${CMS_API_URL}/homepage-components`)
      return response.ok
    } catch (error) {
      return false
    }
  }

  // Fallback content for when CMS is not available
  getFallbackContent(pageName, componentName) {
    const fallbackContent = {
      homepage: {
        hero: {
          title: 'GET READY',
          subtitle: 'UNLEASH YOUR GREATNESS',
          content: 'Join us in shaping the future of technology through innovative education and cutting-edge programs.',
          mediaType: 'video',
          mediaUrl: '/ele.mp4',
          ctaText: 'EXPLORE COURSES',
          ctaLink: '#programs'
        }
      },
      about: {
        hero: {
          title: 'About JRIIT',
          subtitle: 'Empowering Future Technology Leaders',
          content: 'Discover our mission, vision, and commitment to providing world-class IT education in Tanzania.',
          mediaType: 'image',
          mediaUrl: '/assets/about-hero.jpg',
          ctaText: 'Learn More',
          ctaLink: '#mission'
        }
      },
      programs: {
        hero: {
          title: 'Academic Programs',
          subtitle: 'Discover Your Path to Success',
          content: 'Explore our comprehensive range of academic programs designed to prepare you for success in the digital age.',
          mediaType: 'image',
          mediaUrl: '/assets/programs-hero.jpg',
          ctaText: 'Browse Programs',
          ctaLink: '#programs-list'
        }
      },
      contact: {
        hero: {
          title: 'Get in Touch with Us',
          subtitle: 'We\'d Love to Hear from You',
          content: 'Have questions about our programs, admissions, or anything else? We\'d love to hear from you.',
          mediaType: 'image',
          mediaUrl: '/assets/contact-hero.jpg',
          ctaText: 'Contact Us',
          ctaLink: '#contact-form'
        }
      }
    }

    return fallbackContent[pageName]?.[componentName] || null
  }
}

const cmsApiInstance = new CMSApiService();
export default cmsApiInstance;
